/**
 * Auto ALT Text Generator for Shopify Storefront
 * ================================================
 * Automatically generates meaningful, SEO-friendly ALT text for every <img>
 * element that is missing or has an empty ALT attribute.
 *
 * - Works with both Liquid-rendered and dynamically inserted images.
 * - Uses MutationObserver to handle AJAX sections, quick view, cart drawer,
 *   infinite scroll, sliders, popups, and any other dynamic content.
 * - Never overwrites an existing non-empty ALT attribute.
 * - Vanilla JavaScript only — no jQuery or external libraries.
 *
 * ALT text generation priority:
 *   1. Product data from window._themeProducts (via data-product-id)
 *   2. Product title from .m-product-card__name text
 *   3. aria-label on nearest product/collection link
 *   4. Collection title from .m-collection-card__title text
 *   5. Nearby heading (h1–h3) or figcaption
 *   6. Cleaned filename extracted from the image URL
 */

(function () {
  'use strict';

  // -----------------------------------------------------------------------
  // CONFIGURATION
  // -----------------------------------------------------------------------

  /** Marker attribute to prevent re-processing the same image. */
  var PROCESSED_ATTR = 'data-alt-processed';

  /** Maximum ALT text length (SEO best practice). */
  var MAX_ALT_LENGTH = 125;

  /** Debounce delay for MutationObserver callbacks (ms). */
  var DEBOUNCE_MS = 100;

  /**
   * Generic words that should never appear as standalone ALT text.
   * These are filtered out when they are the *only* content.
   */
  var GENERIC_WORDS = [
    'image', 'images', 'img', 'photo', 'photos', 'picture', 'pictures',
    'pic', 'icon', 'logo', 'banner', 'placeholder', 'default', 'untitled',
    'undefined', 'null', 'none', 'blank', 'empty', 'no image', 'no-image',
    'product', 'collection'
  ];

  /**
   * Pattern-only filenames that are not descriptive.
   * Matches things like "image1", "photo_2", "img-003".
   */
  var GENERIC_FILENAME_REGEX = /^(image|img|photo|pic|picture|banner|placeholder|default|product|collection)[\s_\-]?\d*$/i;

  // -----------------------------------------------------------------------
  // UTILITY FUNCTIONS
  // -----------------------------------------------------------------------

  /**
   * Checks whether a given ALT string is "meaningful" — i.e., not empty,
   * not whitespace-only, and not one of the known generic placeholders.
   *
   * @param {string|null|undefined} alt
   * @returns {boolean}
   */
  function isMeaningfulAlt(alt) {
    if (alt == null) return false;
    var trimmed = alt.trim().toLowerCase();
    if (trimmed === '') return false;
    if (GENERIC_WORDS.indexOf(trimmed) !== -1) return false;
    if (GENERIC_FILENAME_REGEX.test(trimmed)) return false;
    return true;
  }

  /**
   * Extracts and cleans a human-readable name from an image URL.
   *
   * Handles Shopify CDN URLs like:
   *   //cdn.shopify.com/s/files/1/0123/4567/8901/files/my-product_500x.png
   *   //cdn.shopify.com/s/files/.../products/my-product.jpg?v=123
   *
   * @param {string} url
   * @returns {string} Cleaned filename or empty string.
   */
  function extractFilename(url) {
    if (!url) return '';

    try {
      // Remove query string and hash
      var clean = url.split('?')[0].split('#')[0];

      // Get the last path segment
      var parts = clean.split('/');
      var filename = parts[parts.length - 1] || '';

      // Remove file extension
      filename = filename.replace(/\.[a-z0-9]{2,5}$/i, '');

      // Remove Shopify image size suffixes like _500x, _100x100, _grande, etc.
      filename = filename.replace(/[_\-](pico|icon|thumb|small|compact|medium|large|grande|original|master|\d+x\d*|\d*x\d+)$/i, '');

      // Remove trailing _crop_center, _crop_top, etc.
      filename = filename.replace(/[_\-]crop[_\-]?\w*/i, '');

      // Replace hyphens, underscores, and dots with spaces
      filename = filename.replace(/[-_.]+/g, ' ');

      // Remove leading/trailing whitespace and collapse internal spaces
      filename = filename.replace(/\s+/g, ' ').trim();

      // Title-case words
      filename = filename.replace(/\b\w/g, function (c) { return c.toUpperCase(); });

      return filename;
    } catch (e) {
      return '';
    }
  }

  /**
   * Cleans and truncates ALT text for SEO best practices.
   *
   * @param {string} text
   * @returns {string}
   */
  function cleanAltText(text) {
    if (!text) return '';

    // Decode HTML entities
    var decoded = text
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ');

    // Strip any remaining HTML tags
    decoded = decoded.replace(/<[^>]*>/g, '');

    // Collapse whitespace
    decoded = decoded.replace(/\s+/g, ' ').trim();

    // Truncate at word boundary if too long
    if (decoded.length > MAX_ALT_LENGTH) {
      decoded = decoded.substring(0, MAX_ALT_LENGTH);
      var lastSpace = decoded.lastIndexOf(' ');
      if (lastSpace > MAX_ALT_LENGTH * 0.6) {
        decoded = decoded.substring(0, lastSpace);
      }
    }

    return decoded;
  }

  /**
   * Returns the trimmed text content of an element, or empty string.
   *
   * @param {Element|null} el
   * @returns {string}
   */
  function getTextContent(el) {
    if (!el) return '';
    return (el.textContent || '').trim();
  }

  // -----------------------------------------------------------------------
  // ALT TEXT GENERATION — CONTEXT STRATEGIES
  // -----------------------------------------------------------------------

  /**
   * Strategy 1: Look up product data from the global _themeProducts object.
   * The Minimog theme stores product JSON keyed by product ID in this global.
   *
   * @param {HTMLImageElement} img
   * @returns {string}
   */
  function getAltFromProductData(img) {
    if (!window._themeProducts) return '';

    // Walk up to the nearest product card container with data-product-id
    var card = img.closest('[data-product-id]');
    if (!card) return '';

    var productId = card.getAttribute('data-product-id');
    if (!productId) return '';

    var product = window._themeProducts[productId];
    if (!product) return '';

    // Determine if this image is the main or hover image for better context
    var isHoverImage = !!img.closest('.m-product-card__hover-image');
    var prefix = product.title || '';

    // Try to add variant context if the image src matches a specific variant
    if (product.variants && product.variants.length > 1) {
      var imgSrc = img.src || img.currentSrc || '';
      for (var i = 0; i < product.variants.length; i++) {
        var variant = product.variants[i];
        if (variant.featured_image && imgSrc.indexOf(variant.featured_image.src) !== -1) {
          // Append variant title if it differs from product title
          if (variant.title && variant.title !== 'Default Title') {
            prefix = product.title + ' - ' + variant.title;
          }
          break;
        }
      }
    }

    if (isHoverImage && prefix) {
      return prefix + ' - Alternate View';
    }

    return prefix;
  }

  /**
   * Strategy 2: Extract product title from the product card's title element.
   *
   * @param {HTMLImageElement} img
   * @returns {string}
   */
  function getAltFromCardTitle(img) {
    // Find nearest product card ancestor
    var card = img.closest('.m-product-card');
    if (card) {
      // Look for the product title element within the card
      var titleEl = card.querySelector('.m-product-card__name');
      var title = getTextContent(titleEl);
      if (title) {
        var isHoverImage = !!img.closest('.m-product-card__hover-image');
        return isHoverImage ? title + ' - Alternate View' : title;
      }
    }
    return '';
  }

  /**
   * Strategy 3: Use aria-label from the nearest link ancestor.
   *
   * @param {HTMLImageElement} img
   * @returns {string}
   */
  function getAltFromAriaLabel(img) {
    // Check the image's direct parent link first
    var link = img.closest('a[aria-label]');
    if (link) {
      var label = link.getAttribute('aria-label');
      if (label && label.trim() && label.trim().toLowerCase() !== 'product title') {
        return label.trim();
      }
    }
    return '';
  }

  /**
   * Strategy 4: Extract collection title from a collection card ancestor.
   *
   * @param {HTMLImageElement} img
   * @returns {string}
   */
  function getAltFromCollectionTitle(img) {
    var collectionCard = img.closest('.m-collection-card');
    if (collectionCard) {
      var titleEl = collectionCard.querySelector('.m-collection-card__title');
      var title = getTextContent(titleEl);
      if (title) {
        return title + ' Collection';
      }
    }
    return '';
  }

  /**
   * Strategy 5: Look for nearby headings (h1–h3), figcaption, or section
   * titles that could describe the image.
   *
   * @param {HTMLImageElement} img
   * @returns {string}
   */
  function getAltFromNearbyContext(img) {
    // Check if the image is inside a <figure> with a <figcaption>
    var figure = img.closest('figure');
    if (figure) {
      var caption = figure.querySelector('figcaption');
      var capText = getTextContent(caption);
      if (capText) return capText;
    }

    // Check for section header (Minimog uses .m-section-heading)
    var section = img.closest('.m-section, section, [class*="shopify-section"]');
    if (section) {
      var heading = section.querySelector('.m-section-heading__text, .m-section-heading h2, h1, h2, h3');
      var headText = getTextContent(heading);
      if (headText && headText.length > 2 && headText.length < 100) {
        return headText;
      }
    }

    // Check the parent container for a heading
    var parent = img.parentElement;
    var depth = 0;
    while (parent && depth < 5) {
      var h = parent.querySelector('h1, h2, h3');
      if (h) {
        var hText = getTextContent(h);
        if (hText && hText.length > 2 && hText.length < 100) {
          return hText;
        }
      }
      parent = parent.parentElement;
      depth++;
    }

    return '';
  }

  /**
   * Strategy 6 (fallback): Clean the image filename for use as ALT text.
   *
   * @param {HTMLImageElement} img
   * @returns {string}
   */
  function getAltFromFilename(img) {
    // Prefer src, fall back to first srcset entry, then data-src (lazy load)
    var url = img.getAttribute('src')
      || (img.getAttribute('srcset') || '').split(',')[0].trim().split(' ')[0]
      || img.getAttribute('data-src')
      || '';

    var filename = extractFilename(url);

    // Reject generic filenames
    if (!filename || GENERIC_FILENAME_REGEX.test(filename)) return '';

    return filename;
  }

  // -----------------------------------------------------------------------
  // MAIN PROCESSING LOGIC
  // -----------------------------------------------------------------------

  /**
   * Generates a meaningful ALT text for a single image element by trying
   * each strategy in priority order.
   *
   * @param {HTMLImageElement} img
   * @returns {string} The generated ALT text, or empty string if none found.
   */
  function generateAltText(img) {
    var strategies = [
      getAltFromProductData,
      getAltFromCardTitle,
      getAltFromAriaLabel,
      getAltFromCollectionTitle,
      getAltFromNearbyContext,
      getAltFromFilename
    ];

    for (var i = 0; i < strategies.length; i++) {
      try {
        var alt = strategies[i](img);
        if (alt && isMeaningfulAlt(alt)) {
          return cleanAltText(alt);
        }
      } catch (e) {
        // Silently continue to the next strategy
      }
    }

    return '';
  }

  /**
   * Processes a single <img> element: checks if it needs ALT text and
   * generates one if necessary.
   *
   * @param {HTMLImageElement} img
   */
  function processImage(img) {
    // Skip if already processed
    if (img.hasAttribute(PROCESSED_ATTR)) return;

    // Mark as processed immediately to prevent duplicate work
    img.setAttribute(PROCESSED_ATTR, '1');

    // Skip if the image already has a meaningful ALT attribute
    var existingAlt = img.getAttribute('alt');
    if (isMeaningfulAlt(existingAlt)) return;

    // Generate and apply ALT text
    var generatedAlt = generateAltText(img);
    if (generatedAlt) {
      img.setAttribute('alt', generatedAlt);
    }
  }

  /**
   * Processes all unprocessed <img> elements in the given root.
   * Uses requestAnimationFrame to avoid blocking rendering.
   *
   * @param {Element|Document} root
   */
  function processAllImages(root) {
    var images = (root || document).querySelectorAll('img:not([' + PROCESSED_ATTR + '])');
    if (images.length === 0) return;

    // Batch DOM writes inside rAF to avoid layout thrashing
    requestAnimationFrame(function () {
      for (var i = 0; i < images.length; i++) {
        processImage(images[i]);
      }
    });
  }

  // -----------------------------------------------------------------------
  // MUTATION OBSERVER — DYNAMIC CONTENT HANDLING
  // -----------------------------------------------------------------------

  var debounceTimer = null;
  var observer = null;

  /**
   * Debounced handler for DOM mutations. Collects all newly added <img>
   * elements and processes them in a single batch.
   *
   * @param {MutationRecord[]} mutations
   */
  function handleMutations(mutations) {
    // Debounce to batch rapid successive mutations (e.g., infinite scroll)
    if (debounceTimer) clearTimeout(debounceTimer);

    debounceTimer = setTimeout(function () {
      var imagesToProcess = [];

      for (var i = 0; i < mutations.length; i++) {
        var addedNodes = mutations[i].addedNodes;
        for (var j = 0; j < addedNodes.length; j++) {
          var node = addedNodes[j];
          if (node.nodeType !== Node.ELEMENT_NODE) continue;

          // Check if the added node itself is an <img>
          if (node.tagName === 'IMG' && !node.hasAttribute(PROCESSED_ATTR)) {
            imagesToProcess.push(node);
          }

          // Check for <img> descendants within the added node
          if (node.querySelectorAll) {
            var imgs = node.querySelectorAll('img:not([' + PROCESSED_ATTR + '])');
            for (var k = 0; k < imgs.length; k++) {
              imagesToProcess.push(imgs[k]);
            }
          }
        }
      }

      if (imagesToProcess.length > 0) {
        requestAnimationFrame(function () {
          for (var i = 0; i < imagesToProcess.length; i++) {
            processImage(imagesToProcess[i]);
          }
        });
      }
    }, DEBOUNCE_MS);
  }

  /**
   * Starts the MutationObserver on document.body to detect dynamically
   * added images (AJAX, quick view, cart drawer, filters, infinite scroll,
   * sliders, popups, etc.).
   */
  function startObserver() {
    if (!window.MutationObserver) return;

    observer = new MutationObserver(handleMutations);
    observer.observe(document.body, {
      childList: true,
      subtree: true
      // Note: We intentionally do NOT observe attributes to keep overhead minimal.
      // Images that change src but not alt are handled on insertion only.
    });
  }

  /**
   * Disconnects the observer (e.g., on page hide for bfcache compatibility).
   */
  function stopObserver() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  }

  // -----------------------------------------------------------------------
  // INITIALIZATION
  // -----------------------------------------------------------------------

  /**
   * Main entry point. Called when the DOM is ready.
   */
  function init() {
    // Process all existing images
    processAllImages(document);

    // Start observing for dynamic images
    startObserver();

    // Re-process on Shopify section load/select events (theme editor + AJAX sections)
    document.addEventListener('shopify:section:load', function () {
      processAllImages(document);
    });

    // Handle bfcache: disconnect on hide, reconnect on show
    window.addEventListener('pagehide', stopObserver);
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) {
        // Page restored from bfcache — re-process and re-observe
        processAllImages(document);
        startObserver();
      }
    });
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already loaded (script loaded with defer or dynamically)
    init();
  }

})();
